/**
 * ====================================================================
 * FIKO CAD GEOMETRY RECOGNIZER
 * ====================================================================
 *
 * Otomatik geometri özellik tespiti.
 * Mesh/Edge'den daire, silindir, koni, küre gibi özellikleri tespit eder.
 * CATIA'nın otomatik özellik tanıma sistemine karşılık gelir.
 *
 * Özellikler:
 * - Daire/Yay tespiti (edge'den)
 * - Silindir tespiti (face'den)
 * - Koni tespiti (face'den)
 * - Küre tespiti (face'den)
 * - Düzlem tespiti (face'den)
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { GeometryReference, ReferenceType } from './DimensionTypes';

// ====================================================================
// GEOMETRY FEATURE TYPES
// ====================================================================

/**
 * Tanınan geometri tipi
 */
export enum RecognizedGeometryType {
    UNKNOWN = 'UNKNOWN',
    POINT = 'POINT',
    LINE = 'LINE',
    CIRCLE = 'CIRCLE',
    ARC = 'ARC',
    ELLIPSE = 'ELLIPSE',
    PLANE = 'PLANE',
    CYLINDER = 'CYLINDER',
    CONE = 'CONE',
    SPHERE = 'SPHERE',
    TORUS = 'TORUS',
    FREEFORM = 'FREEFORM'  // B-spline, NURBS vb.
}

/**
 * Daire/Yay özellikleri
 */
export interface CircleFeature {
    type: RecognizedGeometryType.CIRCLE | RecognizedGeometryType.ARC;
    center: THREE.Vector3;
    radius: number;
    normal: THREE.Vector3;
    startAngle?: number;     // Sadece yay için
    endAngle?: number;       // Sadece yay için
    startPoint?: THREE.Vector3;
    endPoint?: THREE.Vector3;
}

/**
 * Çizgi özellikleri
 */
export interface LineFeature {
    type: RecognizedGeometryType.LINE;
    startPoint: THREE.Vector3;
    endPoint: THREE.Vector3;
    direction: THREE.Vector3;
    length: number;
}

/**
 * Düzlem özellikleri
 */
export interface PlaneFeature {
    type: RecognizedGeometryType.PLANE;
    point: THREE.Vector3;    // Düzlem üzerinde bir nokta
    normal: THREE.Vector3;
    equation: THREE.Vector4; // ax + by + cz + d = 0
}

/**
 * Silindir özellikleri
 */
export interface CylinderFeature {
    type: RecognizedGeometryType.CYLINDER;
    center: THREE.Vector3;   // Merkez nokta (axis üzerinde)
    axis: THREE.Vector3;     // Silindir ekseni
    radius: number;
    height?: number;         // Tespit edilebilirse
}

/**
 * Koni özellikleri
 */
export interface ConeFeature {
    type: RecognizedGeometryType.CONE;
    apex: THREE.Vector3;     // Koni tepesi
    axis: THREE.Vector3;     // Koni ekseni
    halfAngle: number;       // Yarım açı (radyan)
    baseRadius?: number;
    height?: number;
}

/**
 * Küre özellikleri
 */
export interface SphereFeature {
    type: RecognizedGeometryType.SPHERE;
    center: THREE.Vector3;
    radius: number;
}

/**
 * Torus özellikleri
 */
export interface TorusFeature {
    type: RecognizedGeometryType.TORUS;
    center: THREE.Vector3;
    axis: THREE.Vector3;
    majorRadius: number;     // Büyük yarıçap
    minorRadius: number;     // Küçük yarıçap
}

/**
 * Tüm özellik tipleri union
 */
export type GeometryFeature =
    | CircleFeature
    | LineFeature
    | PlaneFeature
    | CylinderFeature
    | ConeFeature
    | SphereFeature
    | TorusFeature
    | { type: RecognizedGeometryType.UNKNOWN }
    | { type: RecognizedGeometryType.FREEFORM };

/**
 * Tanıma sonucu
 */
export interface RecognitionResult {
    success: boolean;
    feature?: GeometryFeature;
    confidence: number;      // 0-1 arası güven skoru
    error?: string;
}

// ====================================================================
// GEOMETRY RECOGNIZER CLASS
// ====================================================================

/**
 * Geometry Recognizer - Otomatik geometri özellik tespiti
 */
export class GeometryRecognizer {
    // Tolerans değerleri
    private static readonly POINT_TOLERANCE = 0.001;      // Nokta eşleşme toleransı
    private static readonly ANGLE_TOLERANCE = 0.01;       // Radyan cinsinden açı toleransı
    private static readonly RADIUS_TOLERANCE = 0.01;      // Yarıçap toleransı (relative)
    private static readonly PLANE_TOLERANCE = 0.001;      // Düzlemsellik toleransı

    // Pre-allocated vectors
    private readonly _center = new THREE.Vector3();
    private readonly _normal = new THREE.Vector3();
    private readonly _axis = new THREE.Vector3();
    private readonly _v0 = new THREE.Vector3();
    private readonly _v1 = new THREE.Vector3();
    private readonly _v2 = new THREE.Vector3();
    private readonly _edge1 = new THREE.Vector3();
    private readonly _edge2 = new THREE.Vector3();

    constructor() {}

    // ====================================================================
    // EDGE/CURVE RECOGNITION
    // ====================================================================

    /**
     * Edge noktalarından geometri özelliği tespit et
     * @param points Edge noktaları (sıralı)
     */
    recognizeEdge(points: THREE.Vector3[]): RecognitionResult {
        if (points.length < 2) {
            return { success: false, confidence: 0, error: 'Yetersiz nokta sayısı' };
        }

        // 2 nokta → Düz çizgi
        if (points.length === 2) {
            return this.recognizeLine(points[0], points[1]);
        }

        // 3+ nokta → Daire/Yay veya Çizgi olabilir
        // Önce düz çizgi kontrolü
        const lineResult = this.checkCollinearity(points);
        if (lineResult.isCollinear) {
            return {
                success: true,
                feature: {
                    type: RecognizedGeometryType.LINE,
                    startPoint: points[0].clone(),
                    endPoint: points[points.length - 1].clone(),
                    direction: new THREE.Vector3()
                        .subVectors(points[points.length - 1], points[0])
                        .normalize(),
                    length: points[0].distanceTo(points[points.length - 1])
                },
                confidence: lineResult.confidence
            };
        }

        // Daire/Yay kontrolü
        const circleResult = this.fitCircle(points);
        if (circleResult.success && circleResult.confidence > 0.9) {
            return circleResult;
        }

        // Tanınamadı
        return {
            success: true,
            feature: { type: RecognizedGeometryType.FREEFORM },
            confidence: 0.5
        };
    }

    /**
     * Düz çizgi tanıma
     */
    private recognizeLine(p1: THREE.Vector3, p2: THREE.Vector3): RecognitionResult {
        const direction = new THREE.Vector3().subVectors(p2, p1).normalize();
        const length = p1.distanceTo(p2);

        return {
            success: true,
            feature: {
                type: RecognizedGeometryType.LINE,
                startPoint: p1.clone(),
                endPoint: p2.clone(),
                direction,
                length
            },
            confidence: 1.0
        };
    }

    /**
     * Noktaların aynı çizgi üzerinde olup olmadığını kontrol et
     */
    private checkCollinearity(points: THREE.Vector3[]): { isCollinear: boolean; confidence: number } {
        if (points.length < 3) {
            return { isCollinear: true, confidence: 1.0 };
        }

        const p0 = points[0];
        const pn = points[points.length - 1];
        const lineDir = new THREE.Vector3().subVectors(pn, p0).normalize();

        let maxDeviation = 0;
        const lineLength = p0.distanceTo(pn);

        for (let i = 1; i < points.length - 1; i++) {
            // Noktanın çizgiye mesafesi
            this._v0.subVectors(points[i], p0);
            const projection = this._v0.dot(lineDir);
            this._v1.copy(p0).addScaledVector(lineDir, projection);
            const deviation = points[i].distanceTo(this._v1);
            maxDeviation = Math.max(maxDeviation, deviation);
        }

        const relativeDeviation = maxDeviation / lineLength;
        const isCollinear = relativeDeviation < GeometryRecognizer.POINT_TOLERANCE * 10;
        const confidence = isCollinear ? 1 - relativeDeviation : relativeDeviation;

        return { isCollinear, confidence };
    }

    /**
     * Daire/Yay fit (3+ nokta için)
     */
    private fitCircle(points: THREE.Vector3[]): RecognitionResult {
        if (points.length < 3) {
            return { success: false, confidence: 0, error: 'Daire için en az 3 nokta gerekli' };
        }

        // İlk 3 noktadan daire merkezi hesapla
        const p1 = points[0];
        const p2 = points[Math.floor(points.length / 2)];
        const p3 = points[points.length - 1];

        const circleParams = this.circleFrom3Points(p1, p2, p3);
        if (!circleParams) {
            return { success: false, confidence: 0, error: 'Daire parametreleri hesaplanamadı' };
        }

        const { center, radius, normal } = circleParams;

        // Tüm noktaların merkeze uzaklığını kontrol et
        let maxRadiusError = 0;
        for (const point of points) {
            // Noktayı daire düzlemine projeksiyon yap
            const toPoint = new THREE.Vector3().subVectors(point, center);
            const projectedDist = Math.abs(toPoint.dot(normal));

            // Düzlemde yarıçap kontrolü
            const inPlaneDist = Math.sqrt(toPoint.lengthSq() - projectedDist * projectedDist);
            const radiusError = Math.abs(inPlaneDist - radius) / radius;
            maxRadiusError = Math.max(maxRadiusError, radiusError);
        }

        const confidence = Math.max(0, 1 - maxRadiusError * 10);

        if (confidence < 0.7) {
            return { success: false, confidence, error: 'Daire uyumu yetersiz' };
        }

        // Tam daire mi yay mı?
        const isFullCircle = this.checkFullCircle(points, center, normal);

        // Açıları hesapla (yay için)
        let startAngle = 0;
        let endAngle = Math.PI * 2;

        if (!isFullCircle) {
            const angles = this.calculateArcAngles(points, center, normal);
            startAngle = angles.startAngle;
            endAngle = angles.endAngle;
        }

        const feature: CircleFeature = {
            type: isFullCircle ? RecognizedGeometryType.CIRCLE : RecognizedGeometryType.ARC,
            center: center.clone(),
            radius,
            normal: normal.clone(),
            startAngle: isFullCircle ? undefined : startAngle,
            endAngle: isFullCircle ? undefined : endAngle,
            startPoint: isFullCircle ? undefined : points[0].clone(),
            endPoint: isFullCircle ? undefined : points[points.length - 1].clone()
        };

        return { success: true, feature, confidence };
    }

    /**
     * 3 noktadan daire parametreleri hesapla
     */
    private circleFrom3Points(
        p1: THREE.Vector3,
        p2: THREE.Vector3,
        p3: THREE.Vector3
    ): { center: THREE.Vector3; radius: number; normal: THREE.Vector3 } | null {
        // Kenar vektörleri
        this._edge1.subVectors(p2, p1);
        this._edge2.subVectors(p3, p1);

        // Normal (daire düzlemi)
        this._normal.crossVectors(this._edge1, this._edge2);
        if (this._normal.lengthSq() < 1e-10) {
            // Noktalar aynı çizgi üzerinde
            return null;
        }
        this._normal.normalize();

        // Circumcenter hesapla (daire merkezi)
        // https://en.wikipedia.org/wiki/Circumscribed_circle#Cartesian_coordinates_2

        const d1 = this._edge1.lengthSq();
        const d2 = this._edge2.lengthSq();
        const cross = this._edge1.clone().cross(this._edge2).lengthSq();

        if (cross < 1e-10) return null;

        const a = d2 * this._edge1.dot(this._edge1.clone().sub(this._edge2)) / (2 * cross);
        const b = d1 * this._edge2.clone().sub(this._edge1).dot(this._edge2) / (2 * cross);

        this._center.copy(p1)
            .addScaledVector(this._edge1, a)
            .addScaledVector(this._edge2, b);

        const radius = this._center.distanceTo(p1);

        return {
            center: this._center.clone(),
            radius,
            normal: this._normal.clone()
        };
    }

    /**
     * Tam daire mi kontrol et
     */
    private checkFullCircle(
        points: THREE.Vector3[],
        center: THREE.Vector3,
        normal: THREE.Vector3
    ): boolean {
        if (points.length < 8) return false;

        // İlk ve son nokta yakın mı?
        const dist = points[0].distanceTo(points[points.length - 1]);
        const radius = center.distanceTo(points[0]);

        // Kapanma mesafesi yarıçapın %5'inden küçükse tam daire
        return dist < radius * 0.05;
    }

    /**
     * Yay açılarını hesapla
     */
    private calculateArcAngles(
        points: THREE.Vector3[],
        center: THREE.Vector3,
        normal: THREE.Vector3
    ): { startAngle: number; endAngle: number } {
        // Referans eksen oluştur
        let xAxis = new THREE.Vector3(1, 0, 0);
        if (Math.abs(normal.dot(xAxis)) > 0.9) {
            xAxis.set(0, 1, 0);
        }
        xAxis.crossVectors(normal, xAxis).normalize();
        const yAxis = new THREE.Vector3().crossVectors(xAxis, normal).normalize();

        // İlk noktanın açısı
        const toStart = new THREE.Vector3().subVectors(points[0], center);
        const startAngle = Math.atan2(toStart.dot(yAxis), toStart.dot(xAxis));

        // Son noktanın açısı
        const toEnd = new THREE.Vector3().subVectors(points[points.length - 1], center);
        const endAngle = Math.atan2(toEnd.dot(yAxis), toEnd.dot(xAxis));

        return { startAngle, endAngle };
    }

    // ====================================================================
    // FACE/SURFACE RECOGNITION
    // ====================================================================

    /**
     * Mesh face'inden geometri özelliği tespit et
     * @param mesh THREE.Mesh objesi
     * @param faceIndex Face index (opsiyonel, tüm mesh için null)
     */
    recognizeFace(mesh: THREE.Mesh, faceIndex?: number): RecognitionResult {
        const geometry = mesh.geometry;
        const positions = geometry.getAttribute('position');
        const normals = geometry.getAttribute('normal');
        const indices = geometry.getIndex();

        if (!positions || !normals) {
            return { success: false, confidence: 0, error: 'Geometry attribute eksik' };
        }

        // Tüm vertexleri topla
        const vertices: THREE.Vector3[] = [];
        const vertexNormals: THREE.Vector3[] = [];

        const matrixWorld = mesh.matrixWorld;
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrixWorld);

        if (faceIndex !== undefined && indices) {
            // Belirli face
            const i = faceIndex * 3;
            for (let j = 0; j < 3; j++) {
                const idx = indices.getX(i + j);
                this._v0.fromBufferAttribute(positions, idx).applyMatrix4(matrixWorld);
                vertices.push(this._v0.clone());

                this._v1.fromBufferAttribute(normals, idx).applyMatrix3(normalMatrix).normalize();
                vertexNormals.push(this._v1.clone());
            }
        } else {
            // Tüm mesh
            for (let i = 0; i < positions.count; i++) {
                this._v0.fromBufferAttribute(positions, i).applyMatrix4(matrixWorld);
                vertices.push(this._v0.clone());

                this._v1.fromBufferAttribute(normals, i).applyMatrix3(normalMatrix).normalize();
                vertexNormals.push(this._v1.clone());
            }
        }

        // Önce düzlem kontrolü
        const planeResult = this.fitPlane(vertices, vertexNormals);
        if (planeResult.success && planeResult.confidence > 0.95) {
            return planeResult;
        }

        // Silindir kontrolü
        const cylinderResult = this.fitCylinder(vertices, vertexNormals);
        if (cylinderResult.success && cylinderResult.confidence > 0.9) {
            return cylinderResult;
        }

        // Küre kontrolü
        const sphereResult = this.fitSphere(vertices);
        if (sphereResult.success && sphereResult.confidence > 0.9) {
            return sphereResult;
        }

        // Koni kontrolü
        const coneResult = this.fitCone(vertices, vertexNormals);
        if (coneResult.success && coneResult.confidence > 0.9) {
            return coneResult;
        }

        // Tanınamadı - freeform
        return {
            success: true,
            feature: { type: RecognizedGeometryType.FREEFORM },
            confidence: 0.5
        };
    }

    /**
     * Düzlem fit
     */
    private fitPlane(vertices: THREE.Vector3[], normals: THREE.Vector3[]): RecognitionResult {
        if (vertices.length < 3) {
            return { success: false, confidence: 0, error: 'Yetersiz vertex' };
        }

        // Ortalama normal hesapla
        const avgNormal = new THREE.Vector3();
        for (const n of normals) {
            avgNormal.add(n);
        }
        avgNormal.normalize();

        // Centroid hesapla
        const centroid = new THREE.Vector3();
        for (const v of vertices) {
            centroid.add(v);
        }
        centroid.divideScalar(vertices.length);

        // Tüm noktaların düzleme mesafesini kontrol et
        let maxDist = 0;
        for (const v of vertices) {
            const dist = Math.abs(new THREE.Vector3().subVectors(v, centroid).dot(avgNormal));
            maxDist = Math.max(maxDist, dist);
        }

        // Bounding box diagonal
        const box = new THREE.Box3().setFromPoints(vertices);
        const diagonal = box.min.distanceTo(box.max);

        const relativeError = maxDist / diagonal;
        const confidence = Math.max(0, 1 - relativeError * 100);

        if (confidence < 0.9) {
            return { success: false, confidence, error: 'Düzlem uyumu yetersiz' };
        }

        // Düzlem denklemi: ax + by + cz + d = 0
        const d = -avgNormal.dot(centroid);

        const feature: PlaneFeature = {
            type: RecognizedGeometryType.PLANE,
            point: centroid.clone(),
            normal: avgNormal.clone(),
            equation: new THREE.Vector4(avgNormal.x, avgNormal.y, avgNormal.z, d)
        };

        return { success: true, feature, confidence };
    }

    /**
     * Silindir fit
     */
    private fitCylinder(vertices: THREE.Vector3[], normals: THREE.Vector3[]): RecognitionResult {
        if (vertices.length < 6) {
            return { success: false, confidence: 0, error: 'Yetersiz vertex' };
        }

        // Silindir ekseni tahmini: Normaller eksene dik olmalı
        // İki normal'in cross product'ı eksen yönünü verir

        const normalPairs: THREE.Vector3[] = [];
        for (let i = 0; i < normals.length - 1; i += 2) {
            const cross = new THREE.Vector3().crossVectors(normals[i], normals[i + 1]);
            if (cross.lengthSq() > 0.01) {
                normalPairs.push(cross.normalize());
            }
        }

        if (normalPairs.length < 2) {
            return { success: false, confidence: 0, error: 'Eksen tespit edilemedi' };
        }

        // Ortalama eksen
        const axis = new THREE.Vector3();
        for (const n of normalPairs) {
            // Yön tutarlılığı için
            if (axis.dot(n) < 0) n.negate();
            axis.add(n);
        }
        axis.normalize();

        // Merkez ve yarıçap hesapla
        // Her vertex için: vertex - (vertex.axis)*axis = radial component
        // Radial component'ların ortalaması merkez

        const radialSum = new THREE.Vector3();
        let radiusSum = 0;

        for (const v of vertices) {
            const axialComponent = v.dot(axis);
            const radial = v.clone().addScaledVector(axis, -axialComponent);
            radialSum.add(radial);
        }

        // Merkez (eksen üzerinde bir nokta)
        const center = radialSum.divideScalar(vertices.length);

        // Yarıçap hesapla
        for (const v of vertices) {
            const axialComponent = v.dot(axis);
            const radial = v.clone().addScaledVector(axis, -axialComponent);
            radiusSum += radial.distanceTo(center);
        }
        const radius = radiusSum / vertices.length;

        // Doğruluk kontrolü
        let maxError = 0;
        for (const v of vertices) {
            const axialComponent = v.dot(axis);
            const radial = v.clone().addScaledVector(axis, -axialComponent);
            const error = Math.abs(radial.distanceTo(center) - radius) / radius;
            maxError = Math.max(maxError, error);
        }

        const confidence = Math.max(0, 1 - maxError * 10);

        if (confidence < 0.8) {
            return { success: false, confidence, error: 'Silindir uyumu yetersiz' };
        }

        const feature: CylinderFeature = {
            type: RecognizedGeometryType.CYLINDER,
            center: center.clone(),
            axis: axis.clone(),
            radius
        };

        return { success: true, feature, confidence };
    }

    /**
     * Küre fit
     */
    private fitSphere(vertices: THREE.Vector3[]): RecognitionResult {
        if (vertices.length < 4) {
            return { success: false, confidence: 0, error: 'Yetersiz vertex' };
        }

        // Centroid tahmini merkez
        const center = new THREE.Vector3();
        for (const v of vertices) {
            center.add(v);
        }
        center.divideScalar(vertices.length);

        // Ortalama yarıçap
        let radiusSum = 0;
        for (const v of vertices) {
            radiusSum += center.distanceTo(v);
        }
        const radius = radiusSum / vertices.length;

        // Hata kontrolü
        let maxError = 0;
        for (const v of vertices) {
            const error = Math.abs(center.distanceTo(v) - radius) / radius;
            maxError = Math.max(maxError, error);
        }

        const confidence = Math.max(0, 1 - maxError * 10);

        if (confidence < 0.8) {
            return { success: false, confidence, error: 'Küre uyumu yetersiz' };
        }

        const feature: SphereFeature = {
            type: RecognizedGeometryType.SPHERE,
            center: center.clone(),
            radius
        };

        return { success: true, feature, confidence };
    }

    /**
     * Koni fit
     */
    private fitCone(vertices: THREE.Vector3[], normals: THREE.Vector3[]): RecognitionResult {
        // Basit koni tespiti - daha gelişmiş algoritmalar gerekebilir
        // Şimdilik placeholder

        return { success: false, confidence: 0, error: 'Koni tespiti henüz tam implemente değil' };
    }

    // ====================================================================
    // GEOMETRY REFERENCE CONVERSION
    // ====================================================================

    /**
     * RecognitionResult'ı GeometryReference'a dönüştür
     */
    toGeometryReference(result: RecognitionResult): GeometryReference | null {
        if (!result.success || !result.feature) {
            return null;
        }

        const feature = result.feature;

        switch (feature.type) {
            case RecognizedGeometryType.CIRCLE:
            case RecognizedGeometryType.ARC:
                return {
                    type: feature.type === RecognizedGeometryType.CIRCLE
                        ? ReferenceType.CIRCLE
                        : ReferenceType.ARC,
                    center: (feature as CircleFeature).center,
                    radius: (feature as CircleFeature).radius,
                    axis: (feature as CircleFeature).normal,
                    startAngle: (feature as CircleFeature).startAngle,
                    endAngle: (feature as CircleFeature).endAngle
                };

            case RecognizedGeometryType.LINE:
                return {
                    type: ReferenceType.EDGE,
                    point: (feature as LineFeature).startPoint,
                    direction: (feature as LineFeature).direction
                };

            case RecognizedGeometryType.PLANE:
                return {
                    type: ReferenceType.FACE,
                    point: (feature as PlaneFeature).point,
                    normal: (feature as PlaneFeature).normal
                };

            case RecognizedGeometryType.CYLINDER:
                return {
                    type: ReferenceType.CYLINDER,
                    center: (feature as CylinderFeature).center,
                    axis: (feature as CylinderFeature).axis,
                    radius: (feature as CylinderFeature).radius
                };

            case RecognizedGeometryType.SPHERE:
                return {
                    type: ReferenceType.SPHERE,
                    center: (feature as SphereFeature).center,
                    radius: (feature as SphereFeature).radius
                };

            case RecognizedGeometryType.CONE:
                return {
                    type: ReferenceType.CONE,
                    center: (feature as ConeFeature).apex,
                    axis: (feature as ConeFeature).axis,
                    radius: (feature as ConeFeature).baseRadius
                };

            default:
                return null;
        }
    }

    // ====================================================================
    // CONVENIENCE METHODS
    // ====================================================================

    /**
     * Mesh'ten otomatik özellik tespiti
     */
    static recognize(mesh: THREE.Mesh): RecognitionResult {
        const recognizer = new GeometryRecognizer();
        return recognizer.recognizeFace(mesh);
    }

    /**
     * Edge noktalarından otomatik özellik tespiti
     */
    static recognizeEdgePoints(points: THREE.Vector3[]): RecognitionResult {
        const recognizer = new GeometryRecognizer();
        return recognizer.recognizeEdge(points);
    }
}
